import { Card, CbhIcon, Text } from "@clipboard-health/ui-components";
import { Stack, useTheme } from "@mui/material";
import {
  RATINGS_COLUMN_WIDTH,
  RATINGS_ICON_WIDTH,
  TIER_ICONS,
  WORKER_FACING_TIER_NAME,
} from "@src/appV2/redesign/Tiers/utils/constants";
import { getTextColorForTier } from "@src/appV2/redesign/Tiers/utils/getTextColorForTier";
import { type NormalizedRatings } from "@src/appV2/redesign/Tiers/utils/types";

type RatingsRequirementsCardProps =
  | {
      matchesWorkerTier: boolean;
      tierType: "GOLD" | "SILVER";
      normalizedRatings: NormalizedRatings;
    }
  | {
      matchesWorkerTier: boolean;
      tierType: "LIMITED";
    };

function getLateCancelsAllowedDisplay(maximumLateCancels: number) {
  switch (maximumLateCancels) {
    case 0: {
      return "0";
    }

    case 1: {
      return "0 or 1";
    }

    default: {
      return `${maximumLateCancels} or fewer`;
    }
  }
}

function NonLimitedRequirementsCard(
  props: Extract<RatingsRequirementsCardProps, { tierType: "GOLD" | "SILVER" }>
) {
  const {
    normalizedRatings: {
      shiftCompletionWithoutCancel,
      shiftCompletionWithoutLateCancel,
      onTimeArrival,
    },
  } = props;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ flexGrow: 1, marginLeft: 3 }}
    >
      {[onTimeArrival, shiftCompletionWithoutCancel, shiftCompletionWithoutLateCancel].map(
        (rating) => {
          const requirementDisplay =
            rating.ratingType === "shiftCompletionWithoutLateCancel"
              ? `${getLateCancelsAllowedDisplay(rating.rating)}`
              : `${rating.display}+`;

          return (
            <Text
              key={rating.ratingType}
              variant="body2"
              align="center"
              sx={{ minWidth: RATINGS_COLUMN_WIDTH }}
            >
              {requirementDisplay}
            </Text>
          );
        }
      )}
    </Stack>
  );
}

export function RatingsRequirementsCard(props: RatingsRequirementsCardProps) {
  const { matchesWorkerTier, tierType } = props;
  const theme = useTheme();
  const tierName = WORKER_FACING_TIER_NAME[tierType];
  const tierColor = getTextColorForTier(tierType, theme);
  const tierIcon = TIER_ICONS[tierType];

  return (
    <Card
      sx={{
        border: `1px solid ${
          matchesWorkerTier ? theme.palette.primary.main : theme.palette.secondary.main
        }`,
        borderRadius: 4,
        ...(matchesWorkerTier && { backgroundColor: theme.palette.primary.main }),
      }}
    >
      {matchesWorkerTier && (
        <Text
          semibold
          variant="body2"
          sx={{
            color: theme.palette.primary.contrastText,
            padding: 1,
            paddingLeft: 4,
          }}
        >
          Your membership
        </Text>
      )}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.common.white,
          paddingX: 6,
          paddingY: 2,
        }}
      >
        <Stack direction="column" alignItems="left" sx={{ width: RATINGS_ICON_WIDTH }}>
          <Stack direction="column" gap={1} alignItems="center" sx={{ width: 30 }}>
            <CbhIcon type={tierIcon} size="large" />
            <Text variant="body2" color={tierColor}>
              {tierName}
            </Text>
          </Stack>
        </Stack>
        {tierType === "LIMITED" ? (
          <Text inline variant="body2" align="center" sx={{ paddingLeft: 5, width: "100%" }}>
            When not qualifying for{" "}
            <Text inline variant="body2" color={theme.palette.tiers?.silver.text}>
              Silver
            </Text>{" "}
            or{" "}
            <Text inline variant="body2" color={theme.palette.tiers?.gold.text}>
              Gold
            </Text>
          </Text>
        ) : (
          <NonLimitedRequirementsCard {...props} />
        )}
      </Stack>
    </Card>
  );
}
